import { memo, PropsWithChildren, type JSX } from 'react';
import {
   Flex,
   FlexProps,
   Popover,
   PopoverProps,
   PopoverTrigger,
   PopoverContent,
   PopoverArrow,
   PopoverBody,
   PopoverContentProps,
   PopoverBodyProps,
} from '@chakra-ui/react';
import ReactDOM from 'react-dom';

function VerticalContentsWrapper(props: FlexProps) {
   return (
      <Flex
         flexDirection="column"
         alignItems="left"
         padding="12px 16px"
         maxHeight="50vh"
         overflowY="auto"
         overflowX="hidden"
         {...props}
      />
   );
}

export type GeneralTooltipProps = PropsWithChildren<
   PopoverProps & {
      content: JSX.Element | string;
      contentProps?: PopoverContentProps;
      appendTo?: () => Element;
   }
>;

const GeneralTooltip = ({
   children,
   content,
   contentProps,
   appendTo,
   ...props
}: GeneralTooltipProps) => {
   const attachTo = appendTo ? appendTo() : undefined;
   return (
      <Popover trigger="click" {...props}>
         <PopoverTrigger>{children}</PopoverTrigger>
         {/**
          * Diagram hotspots are inside the svg. They are the trigger elements,
          * but the tooltip content needs to be rendered outside the svg. This is
          * why we use a portal here.
          */}
         {attachTo ? (
            ReactDOM.createPortal(
               <TooltipContent {...contentProps}>{content}</TooltipContent>,
               attachTo
            )
         ) : (
            <TooltipContent {...contentProps}>{content}</TooltipContent>
         )}
      </Popover>
   );
};

function TooltipContent({
   children,
   ...props
}: Omit<PopoverContentProps, 'children'> & Pick<PopoverBodyProps, 'children'>) {
   return (
      <PopoverContent
         color="black"
         backgroundColor="white"
         borderRadius="md"
         padding="0"
         boxShadow="2xl"
         width="auto"
         {...props}
      >
         <PopoverArrow color="black" backgroundColor="white" />
         <PopoverBody>{children}</PopoverBody>
      </PopoverContent>
   );
}

export default memo(GeneralTooltip);
export { VerticalContentsWrapper };
